// File: src/pages/Signup.jsx
import React, { useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { nativeGoogleToFirebase, nativeAppleToFirebase } from "../nativeSocialAuth";
import api, {
  setAccessToken,
  saveNativeRefreshToken,
  deleteNativeRefreshToken,
} from "../api";
import { googleProvider, appleProvider, startProviderSignIn } from "../firebase";
import { FcGoogle } from "react-icons/fc";
import { FaApple } from "react-icons/fa";
import {
  trackSignup,
  afTrack,
  afSetUserId,
  afGetMediaSource,
  afWaitForMediaSource,
  afGetAttribution,
  afWaitForAttribution,
  afGetWebAttribution,
} from "../analytics/appsflyer";
import {
  captureReferralFromSearch,
  getStoredReferralCode,
  clearStoredReferralCode,
} from "../utils/referrals";

function friendlyAxiosError(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.response?.data?.error;
  if (status) return `(${status}) ${detail || e?.message || "Request failed"}`;
  return e?.message || String(e);
}

export default function Signup({ onSuccess, setToken, fetchUserInfo }) {
  const [email, setEmail] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");

  const [busySocial, setBusySocial] = useState(false);
  const [busyEmail, setBusyEmail] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  
  React.useEffect(() => {
    captureReferralFromSearch(location.search);
  }, [location.search]);

  const redirectPath = useMemo(() => {
    const fromUrl =
      new URLSearchParams(location.search).get("redirect");

    if (fromUrl) return fromUrl;

    try {
      const raw = localStorage.getItem(
        "satstacker_signup_intent"
      );

      if (!raw) return null;

      const saved = JSON.parse(raw);

      if (
        saved?.intent === "smart_timing_trial" &&
        saved?.return_to
      ) {
        return saved.return_to;
      }
    } catch {}

    return null;
  }, [location.search]);

  const isSmartTimingSignup =
    redirectPath === "/plans?smartTiming=1";
  const [notice, setNotice] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [emailSignupComplete, setEmailSignupComplete] = useState(false);
  const [acquisitionSource, setAcquisitionSource] = React.useState("organic");
  const [acquisitionSourceReady, setAcquisitionSourceReady] = React.useState(false);
  const [
    acquisitionAttribution,
    setAcquisitionAttribution,
  ] = React.useState(null);

  const calcMomentum = useMemo(() => {
    try {
      const p = new URLSearchParams(location.search);

      const fromUrl = {
        amount: p.get("amount"),
        freq: p.get("freq"),
        extra_sats: p.get("extra_sats"),
        extra_pct: p.get("extra_pct"),
      };

      if (fromUrl.extra_sats || fromUrl.extra_pct) return fromUrl;

      const saved = JSON.parse(localStorage.getItem("calc_prefill") || "null");
      return saved || null;
    } catch {
      return null;
    }
  }, [location.search]);

  const extraSats = calcMomentum?.extra_sats != null
    ? Number(calcMomentum.extra_sats)
    : null;

  const extraPct = calcMomentum?.extra_pct != null
    ? Number(calcMomentum.extra_pct)
    : null;

  React.useEffect(() => {
    let cancelled = false;

    async function loadAcquisitionSource() {
      try {
        if (
          Capacitor.isNativePlatform()
        ) {
          const [
            src,
            attribution,
          ] = await Promise.all([
            afWaitForMediaSource({
              timeoutMs: 2500,
              pollMs: 250,
            }),

            afWaitForAttribution({
              timeoutMs: 2500,
              pollMs: 250,
            }),
          ]);

          if (!cancelled) {
            setAcquisitionSource(
              attribution?.media_source ||
              src ||
              "organic"
            );

            setAcquisitionAttribution(
              attribution || null
            );

            setAcquisitionSourceReady(
              true
            );
          }

          return;
        }

        const attribution =
          afGetWebAttribution(
            location.search
          );

        const p =
          new URLSearchParams(
            location.search
          );

        const src =
          attribution?.media_source ||
          p.get("af_media_source") ||
          p.get("pid") ||
          p.get("utm_source") ||
          "organic";

        if (!cancelled) {
          setAcquisitionSource(src);

          setAcquisitionAttribution(
            attribution || null
          );

          setAcquisitionSourceReady(
            true
          );
        }
      } catch {
        if (!cancelled) {
          setAcquisitionSource("organic");
          setAcquisitionSourceReady(true);
        }
      }
    }

    loadAcquisitionSource();

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  // Seed calc_prefill from URL params immediately on mount so it survives
  // the email-signup → /login redirect and the web→Capacitor context switch
  React.useEffect(() => {
    try {
      const p = new URLSearchParams(location.search);
      const amount = p.get("amount");
      const freq = p.get("freq");
      const extra_sats = p.get("extra_sats");
      const extra_pct = p.get("extra_pct");
      if (amount && freq) {
        localStorage.setItem("calc_prefill", JSON.stringify({
          amount: Number(amount),
          freq,
          extra_sats: extra_sats ? Number(extra_sats) : null,
          extra_pct: extra_pct ? Number(extra_pct) : null,
        }));
      }
    } catch {}
  }, []);

  const anyBusy = busySocial || busyEmail;

  const didTrackView = React.useRef(false);
  React.useEffect(() => {
    if (didTrackView.current) return;
    afTrack("signup_viewed", {});
    didTrackView.current = true;
  }, []);

  async function getLatestAttribution() {
    let attribution =
      acquisitionAttribution;

    let source =
      acquisitionSource;

    if (
      Capacitor.isNativePlatform()
    ) {
      attribution =
        (await afGetAttribution()) ||
        attribution;

      source =
        attribution?.media_source ||
        (await afGetMediaSource()) ||
        source;
    } else {
      attribution =
        afGetWebAttribution(
          location.search
        ) ||
        attribution;

      source =
        attribution?.media_source ||
        source;
    }

    return {
      source:
        source || "organic",

      attribution:
        attribution || null,
    };
  }

  async function exchangeFirebaseForApiToken(user) {
    const idToken = await user.getIdToken();

    const referralCode = getStoredReferralCode();

    const attributionPayload =
      await getLatestAttribution();

    const { data } = await api.post("/auth/firebase", {
      id_token: idToken,
      remember_me: true,
      acquisition_source:
        attributionPayload.source,

      acquisition_attribution:
        attributionPayload.attribution,
      referral_code: referralCode,
    });

    const accessToken = data?.access_token;

    if (!accessToken) {
      throw new Error("Backend did not return access_token from /auth/firebase");
    }

    if (Capacitor.isNativePlatform()) {
      await deleteNativeRefreshToken();

      if (data?.refresh_token) {
        await saveNativeRefreshToken(
          data.refresh_token,
          user?.email || ""
        );
      }
    }

    return {
      accessToken,
      // Only the backend knows if this auth request inserted a new DB user.
      userCreated: data?.user_created === true,
    };
  }

  async function setAppsFlyerUserIdFromToken(accessToken) {
    try {
      const { data } = await api.get("/me", { headers: { Authorization: `Bearer ${accessToken}` } });
      if (data?.id != null) await afSetUserId(String(data.id));
    } catch (e) {
      console.warn("[AppsFlyer] failed to set user id", e);
    }
  }

  const showApple = useMemo(() => {
    if (!Capacitor.isNativePlatform()) return true;
    return Capacitor.getPlatform() !== "android";
  }, []);

  const handleSocialSignup = async (providerKey) => {
    if (busySocial || busyEmail) return;
    setBusySocial(true);
    afTrack("signup_started", { method: providerKey });
    setError("");
    try {
      let cred;
      if (Capacitor.isNativePlatform()) {
        cred = providerKey === "google" ? await nativeGoogleToFirebase() : await nativeAppleToFirebase();
      } else {
        const provider = providerKey === "google" ? googleProvider : appleProvider;
        cred = await startProviderSignIn(provider);
      }
      const user = cred?.user;
      if (!user) throw new Error("No Firebase user returned");
      const { accessToken, userCreated } =
        await exchangeFirebaseForApiToken(user);
      if (userCreated) {
        const conversionAttribution = await getLatestAttribution().catch(
          () => ({ source: null, attribution: null })
        );
        const conversionParams = {
          method: providerKey,
          ...(conversionAttribution?.source
            ? { acquisition_source: conversionAttribution.source }
            : {}),
          ...(conversionAttribution?.attribution?.campaign
            ? { acquisition_campaign: conversionAttribution.attribution.campaign }
            : {}),
        };
        // Backend confirms a NEW database user, not a linked account.
        trackSignup(providerKey);
        afTrack("signup_success", conversionParams);
      } else {
        afTrack("signup_existing_account", { method: providerKey });
      }
      setAccessToken(accessToken);
      try {
        const attributionPayload =
          await getLatestAttribution();

        await api.patch(
          "/me",
          {
            acquisition_source:
              attributionPayload.source,

            acquisition_attribution:
              attributionPayload.attribution,
          },
          {
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        );
      } catch (error) {
        console.warn(
          "[Attribution] initial social-signup save failed",
          error
        );
      }
      await setAppsFlyerUserIdFromToken(accessToken);
      clearStoredReferralCode();
      if (typeof setToken === "function") setToken(accessToken);
      if (typeof fetchUserInfo === "function") await fetchUserInfo(accessToken);
      try {
        if (!localStorage.getItem("ever_logged_in")) {
          localStorage.setItem("firstLogin", "true");
          localStorage.setItem("ever_logged_in", "1");
        }
        localStorage.setItem("has_logged_in_before", "true");
      } catch {}
      if (typeof onSuccess === "function") { try { onSuccess(); } catch {} }
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      }
    } catch (e) {
      setError(friendlyAxiosError(e) || "Social sign-up failed");
      afTrack("signup_failed", { method: providerKey, error: friendlyAxiosError(e) });
    } finally {
      setBusySocial(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busySocial || busyEmail) return;
    if (pw1 !== pw2) { setError("Passwords don't match"); return; }
    setBusyEmail(true);
    afTrack("signup_started", { method: "email" });
    setError("");
    try {
      const attributionPayload =
        await getLatestAttribution();
      await api.post("/users", {
        email,
        password: pw1,
        acquisition_source:
          attributionPayload.source,

        acquisition_attribution:
          attributionPayload.attribution,
        referral_code: getStoredReferralCode(),
      });
      // /users intentionally returns the same 201 response whether the
      // email is newly inserted or already exists (anti-enumeration).
      // This means a successful HTTP response is NOT proof of new signup.
      // Log the accepted request, then count signup_success ONLY after
      // the one-time email verification endpoint succeeds (VerifyEmail.jsx).
      afTrack("signup_request_submitted", { method: "email" });
      clearStoredReferralCode();

      try {
        localStorage.setItem("firstLogin", "true");
      } catch {}

      // Show the verification screen instead of immediately sending them to login
      setResendEmail(email);
      setShowResend(false);
      setNotice("");
      setEmailSignupComplete(true);
    } catch (err) {
      setError(friendlyAxiosError(err) || "Signup failed");
      afTrack("signup_failed", { method: "email", error: friendlyAxiosError(err) });
    } finally {
      setBusyEmail(false);
    }
  };

  if (emailSignupComplete) {
    const loginUrl = (() => {
      if (redirectPath) {
        return `/login?redirect=${encodeURIComponent(redirectPath)}`;
      }

      const p = new URLSearchParams(location.search);
      const calcParams = p.get("amount") ? `?${p.toString()}` : "";

      return `/login${calcParams}`;
    })();

    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4 auth-safe-page">
        <div className="w-full max-w-sm">

          <button
            type="button"
            onClick={() => setEmailSignupComplete(false)}
            className="mb-8 flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-300 transition"
          >
            ← Back
          </button>

          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg font-bold text-white tracking-tight">
                SatStacker
              </span>
            </div>

            <h1 className="text-2xl font-bold text-white">
              Check your email
            </h1>

            <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
              We sent a verification link to
            </p>

            <p className="text-white font-semibold mt-1 break-all">
              {email}
            </p>

            <p className="text-zinc-500 text-sm mt-4 leading-relaxed">
              Click the link in the email to verify your account. We'll sign you in
              automatically and continue your Smart Timing setup.
            </p>
          </div>

          <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-4 mb-5">
            <p className="text-sm text-zinc-300">
              Didn't receive the email?
            </p>

            <p className="text-xs text-zinc-500 mt-1">
              Check your spam folder, or send another verification email.
            </p>
          </div>

          <button
            type="button"
            disabled={busyEmail}
            onClick={async () => {
              if (!email) return;

              setBusyEmail(true);
              setNotice("");
              setError("");

              try {
                await api.post("/verify-email/resend", {
                  email,
                });

                setNotice("Verification email sent again.");
              } catch (err) {
                setError(
                  friendlyAxiosError(err) ||
                  "Failed to resend verification email."
                );
              } finally {
                setBusyEmail(false);
              }
            }}
            className="w-full py-3 rounded-xl bg-zinc-900 border border-zinc-700 text-white font-semibold text-sm hover:bg-zinc-800 transition disabled:opacity-50"
          >
            {busyEmail ? "Sending…" : "Resend verification email"}
          </button>

          <button
            type="button"
            onClick={() => navigate(loginUrl)}
            className="w-full py-3 rounded-xl font-semibold text-sm mt-3 bg-zinc-900 border border-zinc-700 text-white hover:bg-zinc-800 transition"
          >
            Already verified? Log in
          </button>

          {notice && (
            <div className="mt-4 px-4 py-3 rounded-xl bg-green-950/40 border border-green-800/50">
              <p className="text-sm text-green-400">
                {notice}
              </p>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="mt-4 px-4 py-3 rounded-xl bg-red-950/50 border border-red-800/50 text-red-400 text-sm"
            >
              {error}
            </div>
          )}

        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 auth-safe-page">
      <div className="w-full max-w-sm">

        {/* Back */}
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/login", { replace: true });
          }}
          disabled={busySocial || busyEmail}
          className="mb-8 flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-300 transition disabled:opacity-50"
        >
          ← Back
        </button>

        {/* Heading */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg font-bold text-white tracking-tight">SatStacker</span>
          </div>
          <h1 className="text-2xl font-bold text-white">
            {isSmartTimingSignup
              ? "Start your free month"
              : "Create your account"}
          </h1>

          {extraSats && extraSats > 0 ? (
            <div className="mt-3 rounded-2xl border border-orange-500/25 bg-orange-500/10 px-4 py-3">
              <p className="text-sm text-zinc-300">
                Your simulation showed{" "}
                <span className="font-bold text-orange-400">
                  +{extraSats.toLocaleString()} extra sats
                </span>
                {extraPct && extraPct > 0 ? (
                  <>. That's about <span className="font-bold text-orange-400">+{extraPct.toFixed(2)}%</span> vs standard DCA.</>
                ) : (
                  <> vs standard DCA.</>
                )}
              </p>
              <p className="mt-1 text-xs text-zinc-500">
                Save your result and start stacking with Smart Timing.
              </p>
            </div>
          ) : (
            <div className="mt-2">
              {isSmartTimingSignup ? (
                <>
                  <p className="text-sm text-zinc-300">
                    Create your account to set up Smart Timing.
                  </p>

                  <p className="mt-1 text-xs text-zinc-500">
                    $0 today · 1 month free · then $1.99/month · cancel anytime
                  </p>

                  <p className="mt-2 text-xs text-zinc-400">
                    Uses your existing Strike account · SatStacker never holds your funds
                  </p>
                </>
              ) : (
                <p className="text-zinc-500 text-sm">
                  Free to join. No commitment.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Social */}
        <div className="flex flex-col gap-3 mb-4">
          <button
            type="button"
            disabled={anyBusy}
            onClick={() => handleSocialSignup("google")}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white text-zinc-900 font-semibold text-sm hover:bg-zinc-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FcGoogle size={18} />
            {busySocial ? "Signing up…" : "Continue with Google"}
          </button>

          {showApple && (
            <button
              type="button"
              disabled={anyBusy}
              onClick={() => handleSocialSignup("apple")}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-zinc-900 text-white font-semibold text-sm border border-zinc-700 hover:bg-zinc-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaApple size={16} />
              {busySocial ? "Signing up…" : "Continue with Apple"}
            </button>
          )}
        </div>

        {/* Terms notice */}
        <p className="text-xs text-zinc-600 text-center mb-4 leading-relaxed">
          By signing up, you agree to our{" "}
          <Link to="/terms" className="text-zinc-400 hover:text-brand transition underline">Terms of Service</Link>
          {" "}and{" "}
          <Link to="/privacy" className="text-zinc-400 hover:text-brand transition underline">Privacy Policy</Link>.
        </p>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-zinc-800" />
          <span className="text-zinc-600 text-xs">or sign up with email</span>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>

        {/* Email form */}
        <form onSubmit={handleSubmit} autoComplete="on" method="post" action="/signup" className="flex flex-col gap-3">
          <input
            name="email"
            autoComplete="email"
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={anyBusy}
            className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/30 disabled:opacity-60 transition"
          />

          <input
            id="password"
            name="password"
            autoComplete="new-password"
            type="password"
            placeholder="Password"
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            required
            disabled={anyBusy}
            className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/30 disabled:opacity-60 transition"
          />

          <input
            id="confirm-password"
            name="confirm-password"
            autoComplete="new-password"
            type="password"
            placeholder="Confirm password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            required
            disabled={anyBusy}
            className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/30 disabled:opacity-60 transition"
          />

          <button
            type="submit"
            disabled={busyEmail || busySocial}
            className={`w-full py-3 rounded-xl font-bold text-sm transition mt-1
              ${busyEmail || busySocial
                ? "bg-orange-500/50 text-white/70 cursor-not-allowed"
                : "bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white shadow-lg shadow-orange-500/15"
              }`}
          >
            {busyEmail
              ? "Creating account…"
              : isSmartTimingSignup
                ? "Continue to Smart Timing →"
                : "Create account"}
          </button>
        </form>

        {/* Error */}
        {error && (
          <div role="alert" className="mt-4 px-4 py-3 rounded-xl bg-red-950/50 border border-red-800/50 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="mt-7 pt-6 border-t border-zinc-900 flex flex-col gap-2">
          <p className="text-sm text-zinc-500">
            Already have an account?{" "}
            <Link
              to={redirectPath ? `/login?redirect=${encodeURIComponent(redirectPath)}` : "/login"}
              className="text-brand hover:underline font-medium"
            >
              Log in
            </Link>
          </p>
          <Link
            to="/partner-start"
            className="text-sm text-zinc-500 hover:text-brand transition"
          >
            Used Smart Timing through a partner?{" "}
            <span className="text-brand font-medium">View your stats</span>
          </Link>
          <button
            type="button"
            disabled={anyBusy}
            onClick={async () => {
              if (!showResend) { setShowResend(true); return; }
              if (!resendEmail) return;
              try {
                await api.post("/verify-email/resend", { email: resendEmail });
                setNotice("If that address exists and isn't verified, we've re-sent the e-mail.");
                setShowResend(false);
                setResendEmail("");
              } catch {
                setNotice("Failed to resend verification e-mail.");
              }
            }}
            className="text-sm text-zinc-600 hover:text-zinc-400 transition text-left disabled:opacity-50 w-fit"
          >
            Resend verification e-mail
          </button>
          {showResend && (
            <div className="flex gap-2 mt-2">
              <input
                type="email"
                placeholder="your@email.com"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:border-brand"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!resendEmail) return;
                  try {
                    await api.post("/verify-email/resend", { email: resendEmail });
                    setNotice("If that address exists and isn't verified, we've re-sent the e-mail.");
                    setShowResend(false);
                    setResendEmail("");
                  } catch {
                    setNotice("Failed to resend verification e-mail.");
                  }
                }}
                className="px-3 py-2 rounded-lg bg-brand text-black text-sm font-semibold"
              >
                Send
              </button>
            </div>
          )}
          {notice && (
            <p className="text-sm text-green-400 mt-2">{notice}</p>
          )}
        </div>

      </div>
    </div>
  );
}